import { RefObject, MutableRefObject, CSSProperties } from 'react';
import { MutationFunction, QueryObserverResult } from 'react-query';
import {
  IUser,
  IBooking,
  IDelta,
  IUserDocumentSection,
  IUserDocumentTemplate,
  IAvailability,
  IBlockTime,
  IPromptCard,
  ITextBlock,
  IChecklist,
  IDocumentRef,
  IDocumentParagraph,
  IViewDocument,
  IDocument,
  IForm,
  IUpdateDocumentResponse,
  IOrganization,
} from '@homeporter/types';
import { DailyParticipant } from '@daily-co/daily-js';

export interface ICalendarEvent {
  start: Date;
  end: Date;
  title: string;
  details?: IBooking;

  homeOwnerName?: string;
  sessionType?: string;
  city?: string;
  province?: string;
  promoCode?: Boolean;
}

export interface IPlan {
  id: number;

  amount: number;
  bundle_limit: number;
  business_rules: any[];
  button_title: string;
  created_at: string;
  description: string;
  expiry_in_days: number | null;
  features: any[];
  general_business_policy: string;
  icon: string;
  label: string;
  name: string;
  notification_schedule: any;
  onsched_location_id: number | null;
  resource_id: number;
  onsched_round_robin: number;
  service_id: number;
  optional_address: boolean;
  optional_verify_mobile: boolean;
  organization_id: number;
  plan_id: number;
  session_duration: number;
  sms_vhc_invite_message: string;
  status: number;
  summary: string;
  updated_at: string;
}

export interface IBookingsFilter {
  search?: string;
  sessionType?: (string | number)[];
  startDate?: Date | null;
  endDate?: Date | null;
  region?: string;
  status?: string | number;
  promoCode?: boolean;
}

export interface IUserInfo {
  email: string;
  firstname: string;
  lastname: string;
  mobile_number?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  province?: string;
  country_code?: string;
}
/*
export interface IBookingAttachment {
  id: number;
  name: string;
  type: string;
  size: number;
  url: string;
  last_modified: number;
  uploaded_at: string;
  file?: File;
}

export interface IBooking {
  booking_id?: number;
  payment_id: number;
  resource_id: string;
  confirmation_code: string;
  session_duration: number;
  inspector_id: number;
  inspector?: any;
  owner?: boolean;
  inspector_owner?: {
    user_id: number;
    email: string;
  };
  shared_with?: number[];
  title: string;
  description: string;
  mobile_number: string;
  mobile_status: string;
  attachments?: IBookingAttachment[];
  tasks?: Record<string, boolean>;
  payment: {
    reference: string;
    plan: {
      label: string;
    };
    user: {
      firstname?: string;
      lastname?: string;
      email: string;
    };
  };
  mobile_details: string;
  booking_session_code: string;
  guest_entry_code: string;
  authorization_code: string;

  address: string;
  city: string;
  province: string;
  postal_code: string;
  country_code: string;

  status: number;
  booking_date: string;
  created_at: Date | string;
  updated_at: Date | string;
  transcript_exists?: boolean;
  transcript_v2_exists?: boolean;
  document_template_name?: string;
  timezone?: string;
  timezone_name?: string;
  booking_secret?: string;
  join_code?: string;
}
*/

type MutationFn = MutationFunction<any, any>;

export type HomeViewType = 'schedule' | 'calendar' | 'reports' | 'homeowners';

export type BookingListType = 'active' | 'archived' | 'month';

export interface IBookings {
  gitHash: string;
  bookingsFilter: IBookingsFilter;
  setBookingsFilter: (_: IBookingsFilter) => void;

  bookings: IBooking[];
  isFetchingBookings: boolean;
  errorFetchingBookings: boolean;

  bookingListType: BookingListType;
  setBookingListType: (view: BookingListType) => void;

  homeView: HomeViewType;
  setHomeView: (_: HomeViewType) => void;

  refetchBookings: () => Promise<QueryObserverResult<any, unknown> | undefined>;
  isFetchingSessionDetails: boolean;

  groupedBookings: any[];

  description: string;
  yearBuilt: string;
  purchaseDate: string;
  contextPresent: boolean;
  setContextPresent: (present: boolean) => void;
  context: string;
  setContext: (_: string) => void;

  isBookingModalOpen: boolean;
  setIsBookingModalOpen: (_: boolean) => void;
  bookingModalUrl: string;
  setBookingModalUrl: (_: string) => void;

  isManageReportModalOpen: boolean;
  setIsManageReportModalOpen: (_: boolean) => void;

  profile?: IProfile;
  isLoading: boolean;
  updateProfile?: (_: IProfile) => Promise<void>;

  userSettings: IUserSettings;
  updateUserSettings: ({
    settings,
    services,
  }: {
    settings: IUserSettings;
    services?: number[];
  }) => Promise<any>;

  organization: IOrganization;

  setSelectedBooking: (_: any) => void;
  selectedBooking: any;

  updateBookingStatus?: MutationFn;
  updateStatusError?: boolean;

  updateDetails?: MutationFn;
  updateDetailsLoading?: boolean;
  updateDetailsError?: boolean;
  isSessionSummaryModalOpen: boolean;
  setIsSessionSummaryModalOpen: (state: boolean) => void;

  plans: IPlan[];
  setPlans: (plans: IPlan[]) => void;
  updatePlan: (_: IPlan) => Promise<void>;

  services: number[];
  setServices: (services: number[]) => void;
  documentTemplates: IUserDocumentTemplate[];
  setDocumentTemplates: (_: IUserDocumentTemplate[]) => void;
  updateDocumentTemplate: (_: IUserDocumentTemplate) => Promise<void>;

  currentView: string;
  setCurrentView: (view: 'active' | 'archived') => void;

  todaysBookings: any;

  getIconStyle: (title: string) => React.CSSProperties;

  toolbarLabel: string;
  setToolbarLabel: (label: string) => void;

  isDeleteBookingModalOpen: boolean;
  setIsDeleteBookingModalOpen: (state: boolean) => void;

  filters: IBookingsFilter;
  setFilters: (filters: IBookingsFilter) => void;

  filterResults: IBooking[];
  setFilterResults: (results: IBooking[]) => void;

  currentDate: Date;
  setCurrentDate: (date: Date) => void;

  handleToolbarNavigate: (
    action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE',
    newDate?: Date,
  ) => void;

  selectedPlan: IPlan | null;
  setSelectedPlan: (plan: IPlan | null) => void;

  createdBooking: IBooking | null;
  setCreatedBooking: (booking: IBooking | null) => void;
  userInfo: IUserInfo | null;
  setUserInfo: (user: IUserInfo | null) => void;
  getUser: (_: number) => Promise<IUser | null>;

  editBooking: IBooking | null;
  setEditBooking: (booking: IBooking | null) => void;
}

export interface IInspector {
  name: string;
  email: string;
  phone_number: string;
}
export interface IClient extends IInspector {
  sub: string;
  family_name: string;
}
export interface IChat {
  date: string;
  id: string;
  message: string;
  senderId: string;
}

export interface ICanvas {
  width: number;
  height: number;
}

export interface IAnalysis {
  query: string;
  response: string;
  model: string;
}

export interface ISpeakers {
  uid: string;
}

export interface ISpeakerSegments {
  uid: string;
  sids: string[];
}

export interface ISpeakerTranscript {
  speakers: ISpeakers;
  segments: ISpeakerSegments[];
}

export interface ISpeakers {
  [uid: string]: string;
}

export interface ISpeakerSegments {
  uid: string;
  sids: string[];
}

export interface ISpeakerTranscript {
  speakers: ISpeakers;
  segments: ISpeakerSegments[];
}

export interface IAnalysis {
  query: string;
  response: string;
  model: string;
}
export interface IImage {
  type: string;
  processed?: boolean;
  id?: string;
  height: number;
  width: number;
  ratio?: number;
  src: string;
  strokes: any[];
  layer?: number;
  description?: IAnalysis[];
  date?: string;
  time?: number;

  thumb: {
    height: number;
    width: number;
    src: string;
  };
}

export interface ITranscript {
  type?: string;
  _processes?: boolean;
  processes?: boolean;
  id?: string;
  processed?: boolean;
  duration_seconds?: number;
  is_final?: boolean;
  session_id: string;
  speech_final?: boolean;
  speech_timestamp?: number;
  text?: string;
  timestamp?: string;
  time?: number;
  transcription_delay?: number;

  user_id: string;
  user_name: string;
}
export interface INote {
  title: string;
  description: string;
  idx: number;
  src: string;
}
export interface IAttachment {
  id: string;
  delta?: IDelta;
  note?: INote;
}

export type ITranscriptPart = (ITranscript | IImage) & {
  _processed?: boolean;
};

export interface IRenderTranscript {
  merged?: boolean;
  user_name?: string;
  text?: string;
  image_id?: string;
}

export interface ITranscriptSegment {
  merged?: boolean;
  user_name?: string;
  user_id?: string;
  sentences?: ITranscriptSentence[];
  image_id?: string;
  timeStamp: string;
  time: number;
  displayTime?: string;
}

export interface ITranscriptSentence {
  sid: string;
  text: string;
}

export interface ISearchResults {
  sid: string;
  text?: string;
  scores: IAIScore[];
}

export interface IClassifierResults {
  sid: string;
  scores: number;
}

export interface IParagraphQueue {
  did: number;
  idx: number;
}
export interface IAIScore extends IParagraphQueue {
  score: number;
  text?: string;
  ref?: IDocumentRef;
}

export interface IAI {
  sid?: string;
  search?: string[];
  filters?: string[];
}
export type TranscribeStateType =
  | 'on'
  | 'off'
  | 'start'
  | 'starting'
  | 'stop'
  | 'stopping'
  | 'error';

export interface ISessionData extends IBooking {
  client: IClient;
  inspector: IInspector;
  session_chats: IChat[];
  session_images: IImage[];
  session_transcripts: ITranscript[];
}

export interface ISession {
  setSession: (booking: IBooking | undefined, data?: any) => void;
  resetSession: () => void;
  setSessionTranscript: (booking: IBooking, data: any) => void;
  resetSessionTranscript: () => void;

  localParticipantId: string;
  setLocalParticipantId: (_: string) => void;

  client: any;
  inspector: any;
  booking: IBooking | undefined;
  setBooking: (_: IBooking | undefined) => void;

  images: string[];
  setImages: (_: string[]) => void;

  transcriptParts: ITranscriptPart[];
  setTranscriptParts: React.Dispatch<React.SetStateAction<ITranscriptPart[]>>;
  appendImage: (_: IImage) => void;

  chatHistory: IChat[];
  setChatHistory: (_: any) => void;

  hasUnreadChat: boolean;
  setHasUnreadChat: (_: boolean) => void;

  transcriptSegments: ITranscriptSegment[];
  setTranscriptSegments: React.Dispatch<
    React.SetStateAction<ITranscriptSegment[]>
  >;

  transcribeState: TranscribeStateType;
  setTranscribeState: (_: TranscribeStateType) => void;

  canvasSketchSize: ICanvas;
  setCanvasSketchSize: (canvas: ICanvas) => void;

  isImageLoading: boolean;
  setIsImageLoading: (_: boolean) => void;

  hasUpdatedImages: boolean;
  setHasUpdatedImages: (_: boolean) => void;

  imagesMapRef: MutableRefObject<Map<string, IImage> | null>;
  replaceImageUrl: (image: IImage) => IImage;

  selectedImage: IImage | null;
  setSelectedImage: React.Dispatch<React.SetStateAction<IImage | null>>;

  selectedImageElement: HTMLImageElement | null;
  setSelectedImageElement: (img: HTMLImageElement | null) => void;

  strokesLayer: number;
  setStrokesLayer: (val: number) => void;

  inspectorPlaceholderText: string;
  canvasDisplayRef: RefObject<HTMLCanvasElement> | null;

  zoom: number;
  setZoom: (zoom: number) => void;

  backgroundSettings: IBackgroundSettings;
  setBackgroundSettings: (val: IBackgroundSettings) => void;

  remoteParticipant: DailyParticipant | undefined;
  setRemoteParticipant: (val: DailyParticipant | undefined) => void;

  agentParticipantId: string;
  setAgentParticipantId: (_: string) => void;

  sipSessionId: string;
  setSipSessionId: (_: string) => void;

  transcriptAttachments: Record<string, (IDelta | INote)[]>;
}

export interface ICopilot {
  //use extends
  filterTranscriptContext: string;
  setFilterTranscriptContext: (val: string) => void;

  searchResults: any;
  clearSearchResults: () => void;
  searchLoading: boolean;
  searchError: boolean;
  updateSentence: (val: ITranscriptSentence) => void;

  copilotEnabled: boolean;
  setCopilotEnabled: (_: boolean) => void;

  aiMode: string;
  setAiMode: (val: string) => void;

  filterHomeSystems: boolean;
  setFilterHomeSystems: (_: boolean) => void;

  scoreThreshold: number;
  setScoreThreshold: (val: number) => void;

  selectedSentenceId: string;
  setSelectedSentenceId: (val: string) => void;

  searchEnqueue: (_: ITranscriptSentence) => void;

  storeTranscriptEmbeddingsLoading: boolean;
  storeTranscriptEmbeddingsError: boolean;

  documentLoading: boolean;
  documentError: boolean;
  clearError: () => void;
  updateDocument: (doc: Partial<IDocument>) => Promise<IUpdateDocumentResponse>;
  bookList: IDocument[];
  manualList: IDocument[];
  checklistList: IDocument[];
  formList: IDocument[];
  setFormList: React.Dispatch<React.SetStateAction<IDocument[]>>;
  refetchDocuments: () => void;
  viewDocument: IViewDocument | null;
  setViewDocument: (_: IViewDocument | null) => void;

  setDocumentParagraphQueue: (val: IParagraphQueue[]) => void;
  documentParagraphMapRef: MutableRefObject<Map<
    string,
    IDocumentParagraph
  > | null>;

  transcriptSentenceMapRef: MutableRefObject<Map<
    string,
    ITranscriptSentence
  > | null>;
  transcriptSearchResultsMapRef: MutableRefObject<Map<
    string,
    ISearchResults
  > | null>;
  classifyTranscriptResultsMapRef: MutableRefObject<Map<string, number> | null>;
  transcriptSentenceEmbeddingsQueueMapRef: MutableRefObject<Map<
    string,
    number
  > | null>;

  filterTranscriptResultsMap: Map<string, number>;
  setFilterTranscriptResultsMap: (_: Map<string, number>) => void;

  setStoreTranscriptEmbeddingsQueue: (_: any) => void;

  filterTranscriptPrompt: string;
  setFilterTranscriptPrompt: (_: string) => void;
  filterTranscriptResult: (_: string) => void;
  filterTranscriptLoading: boolean;
  filterTranscriptError: boolean;

  filterModelList: string[];
  filterModel: number;
  setFilterModel: (_: number) => void;

  homeSystemOptions: string[];
  selectedClassifierOptions: number[];
  setSelectedClassifierOptions: (_: number[]) => void;

  applyClassifier: string[];
  setApplyClassifier: (_: string[]) => void;

  filterTranscriptWithDecoder: () => void;
  isFilteringTranscript: number;
  errorFilteringTranscript: boolean;
  setErrorFilteringTranscript: (_: boolean) => void;

  storeFilterResults: () => void;
  filterTranscriptResultsStore: IFilterTranscriptResultsStore[];
  setFilterTranscriptResultsStore: (_: any) => void;
}

export interface IRoom {
  id: string;
  name: string;
  api_created: boolean;
  privacy: string;
  url: string;
  created_at: string;
  config: {
    exp: number;
  };
}

export interface IShapes {
  [key: string]: number;
}

export interface IPoint {
  x: number;
  y: number;
}
export interface ICallbackFunction {
  (...args: any[]): void;
}

export interface IStrokes {
  [key: string]: number;
}

export interface IBackgroundSettings {
  type: string;
  index: number;
}

export type TabType =
  | ''
  | 'Copilot'
  | 'Chat'
  | 'Images'
  | 'Appointment Description'
  | 'Map'
  | 'Cards'
  | 'Streetview'
  | 'Transcripts';

interface _function {
  (sid: string): void;
}
export interface IDocumentEditor {
  booking: IBooking | null;
  setBooking: React.Dispatch<React.SetStateAction<IBooking | null>>;

  selectedModel: string;
  setSelectedModel: (_: string) => void;

  regenerateIndex: number | undefined;
  setRegenerateIndex: (_?: number | undefined) => void;

  quillRef: React.RefObject<any>;
  documentRef: React.RefObject<any>;

  filters: string[];
  setFilters: (filter: string[]) => void;

  isGeneratingResponse: boolean;
  errorGeneratingResponse: Error | unknown;

  fetchDocumentContents: () => void;
  isFetchingDocumentContents: boolean;
  errorFetchingDocumentContents: Error | unknown;

  saveEnabled: boolean;
  setSaveEnabled: (_: boolean) => void;

  selectedCard: ISelectedCard | null;
  setSelectedCard: (id: ISelectedCard | null) => void;

  showGenerateReportPanel: boolean;
  setShowGenerateReportPanel: (_: boolean) => void;

  saveChanges: () => void;

  documentIndex: number;
  setDocumentIndex: (_: number) => void;

  documentSections: IUserDocumentSection[];
  setDocumentSections: React.Dispatch<
    React.SetStateAction<IUserDocumentSection[]>
  >;
  documentSection: IUserDocumentSection | null;
  setDocumentSection: React.Dispatch<
    React.SetStateAction<IUserDocumentSection | null>
  >;

  documentsRef: React.RefObject<IUserDocumentTemplate[]>;
  saveTemplateButtonRef: React.MutableRefObject<(() => void) | null>;

  setDocumentContents: (_: IUserDocumentTemplate[]) => void;
  appendNewDocument: (title: string) => void;

  isResettingDocumentContents?: boolean;
  errorResettingDocumentContents?: Error | unknown;
  resetDocumentSection: () => void;

  activeTab: TabType;
  setActiveTab: (_: TabType) => void;

  defaultMessagesTemplate: string;

  highlightedSids: string[];
  setHighlightedSids: React.Dispatch<React.SetStateAction<string[]>>;

  highlightSentence: _function | undefined;
  setHighlightSentence: (_: _function | undefined) => void;

  scrollTo: string | undefined;
  setScrollTo: (_: string | undefined) => void;
}

export interface ICanvasSketch {
  canvasSketchRef: RefObject<HTMLCanvasElement> | null;
  setSendPayload: ICallbackFunction;

  color: string;
  setColor: (color: string) => void;

  penSize: number;
  setPenSize: (size: number) => void;

  activeTab: string;
  setActiveTab: (tab: string) => void;

  activeShape: string;
  setActiveShape: (shape: string) => void;

  strokeList: any[];
  setStrokeList: (list: any[]) => void;

  isStrokeListUpdated: boolean;
  setIsStrokeListUpdated: (_: boolean) => void;

  isPenVisible: boolean;
  setIsPenVisible: (visible: boolean) => void;

  isLayersVisible: boolean;
  setIsLayersVisible: (visible: boolean) => void;

  croppedUndoImages: any;
  setCroppedUndoImages: (images: any[]) => void;

  handleClickOutside: () => void;

  isColorPaletteOpen: boolean;
  setIsColorPaletteOpen: (isOpen: boolean) => void;

  handleToggleShapesModal: () => void;

  isShapesVisible: boolean;
  setIsShapeVisible: (isVisible: boolean) => void;

  handleToggleColorPalette: (isVisible: boolean) => void;

  shapesRef: React.RefObject<any>;
  paletteRef: React.RefObject<any>;

  handleSlider: (event: Event, value: number | number[]) => void;

  handleSetActiveShape: (shape: string) => void;

  handleSelectColor: (color: { hex: string }) => void;

  handleSaveImage: () => void;

  mouseDown: (event: any) => void;
  mouseMove: (event: any) => void;
  mouseUp: () => void;
  mouseOut: () => void;

  draw: (canvasSketch: any, strokes: any, clear?: boolean) => void;
  undoChanges: () => void;
  clearCanvas: () => void;
}

export interface IChunk {
  sentences: string;
  sids: string[];
  timestamp: number;
}
export interface IGenerateRequest {
  bookingId: number;
  regenerateIndex: number;
  messages?: object[];
  model?: string;
  algorithm?: string;
  name?: string;
  type: 'Prompt' | 'Checklist' | 'Transcript' | 'Form';
  chunks?: IChunk[];
  index?: number;
  criteria?: string;
  context?: string;
  temperature?: number;
}

export interface IResponseTranscript {
  cleanedTranscript: string;
  context: string;
}
export interface IResponseChecklist extends IResponseTranscript {
  checklistCriteria: string;
}

export interface IResponseForm {
  text: string;
  usage: any;
  id: string;
  model: string;
  message_id: string;
}

export interface ISelectedCard {
  index: number;
  obj: IPromptCard | ITextBlock | IChecklist | IForm;
}

export interface BookingFilters {
  search?: string;
  sessionType?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  city?: string;
  status?: BookingStatus;
  promoCode?: Boolean;
}

export enum BookingStatus {
  All = -1,
  Pending = 0,
  Open = 1,
  SessionStarted = 2,
  SessionCompleted = 3,
  SessionCanceled = 4,
  ReportStarted = 5,
  ReportUploaded = 6,
  ReportSent = 7,
}

export interface CalendarEvent {
  start: Date;
  end: Date;
  title: string;
  details?: IBooking;
}

export interface TableCalendarEvent {
  date: string;
  time: string;
  description?: string;
  sessionType?: string;
  title: string;
  start?: string;
  end?: string;
  details?: {
    title?: string;
    description?: string;
  };
}

export interface IResponseData {
  bookingId: number | undefined;
  regenerateIndex: number;
  response?: string;
  message?: string;
  complete?: boolean;

  message_id?: string;
  model?: string;
  usage?: any;
}

export interface IFilterTranscriptResultsStore {
  model: string;
  prompt: string;
  context: string;

  results: Record<string, number>;
}

export interface CalendarEvent {
  start: Date;
  end: Date;
  title: string;
  details?: IBooking;
}

export interface ISmsSendStatus {
  status: string;
  message: string;
}

export interface IMediaStatus {
  event: string;
  status: string;
  expanded?: boolean;
  data: {
    error?: string | Error;
    audioTrackLabel?: string;
    videoTrackLabel?: string;
    userAgent?: string;
    pageName?: string;
    uri?: string;
    accountSid?: string;
    sid?: string;
  };
  timestamp?: string;
}

export interface IUserSettings {
  fontSize?: number;
  dev?: boolean;
  supervisor?: boolean;
  stats?: boolean;
  timezone?: string;
  availability?: IAvailability[];
  blockTime?: IBlockTime[];
  bookingLeadTime?: number;
  bookingAvailabilityWindow?: number;
}
export interface IProfile {
  user_id: number;
  organization_id: number;
  firstname: string;
  lastname: string;
  email: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  documentTemplates?: IUserDocumentTemplate[];
  settings?: IUserSettings;
  role?: string;
  onsched_resource_id?: string;
  sub?: string;
  resource_id?: string;
}

export const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface ICopilot {
  //use extends
  filterTranscriptContext: string;
  setFilterTranscriptContext: (val: string) => void;

  searchResults: any;
  clearSearchResults: () => void;
  searchLoading: boolean;
  searchError: boolean;
  updateSentence: (val: ITranscriptSentence) => void;

  copilotEnabled: boolean;
  setCopilotEnabled: (_: boolean) => void;

  aiMode: string;
  setAiMode: (val: string) => void;

  filterHomeSystems: boolean;
  setFilterHomeSystems: (_: boolean) => void;

  scoreThreshold: number;
  setScoreThreshold: (val: number) => void;

  selectedSentenceId: string;
  setSelectedSentenceId: (val: string) => void;

  searchEnqueue: (_: ITranscriptSentence) => void;

  storeTranscriptEmbeddingsLoading: boolean;
  storeTranscriptEmbeddingsError: boolean;

  documentLoading: boolean;
  documentError: boolean;
  clearError: () => void;
  updateDocument: (doc: Partial<IDocument>) => Promise<IUpdateDocumentResponse>;
  bookList: IDocument[];
  manualList: IDocument[];
  checklistList: IDocument[];
  formList: IDocument[];
  setFormList: React.Dispatch<React.SetStateAction<IDocument[]>>;
  viewDocument: IViewDocument | null;
  setViewDocument: (_: IViewDocument | null) => void;

  setDocumentParagraphQueue: (val: IParagraphQueue[]) => void;
  documentParagraphMapRef: MutableRefObject<Map<
    string,
    IDocumentParagraph
  > | null>;

  transcriptSentenceMapRef: MutableRefObject<Map<
    string,
    ITranscriptSentence
  > | null>;
  transcriptSearchResultsMapRef: MutableRefObject<Map<
    string,
    ISearchResults
  > | null>;
  classifyTranscriptResultsMapRef: MutableRefObject<Map<string, number> | null>;
  transcriptSentenceEmbeddingsQueueMapRef: MutableRefObject<Map<
    string,
    number
  > | null>;

  filterTranscriptResultsMap: Map<string, number>;
  setFilterTranscriptResultsMap: (_: Map<string, number>) => void;

  setStoreTranscriptEmbeddingsQueue: (_: any) => void;

  filterTranscriptPrompt: string;
  setFilterTranscriptPrompt: (_: string) => void;
  filterTranscriptResult: (_: string) => void;
  filterTranscriptLoading: boolean;
  filterTranscriptError: boolean;

  filterModelList: string[];
  filterModel: number;
  setFilterModel: (_: number) => void;

  homeSystemOptions: string[];
  selectedClassifierOptions: number[];
  setSelectedClassifierOptions: (_: number[]) => void;

  applyClassifier: string[];
  setApplyClassifier: (_: string[]) => void;

  filterTranscriptWithDecoder: () => void;
  isFilteringTranscript: number;
  errorFilteringTranscript: boolean;
  setErrorFilteringTranscript: (_: boolean) => void;

  storeFilterResults: () => void;
  filterTranscriptResultsStore: IFilterTranscriptResultsStore[];
  setFilterTranscriptResultsStore: (_: any) => void;
}
